/**
 * RevenueCat Purchases Service - Web
 * Uses @revenuecat/purchases-js SDK for web payments via Stripe
 */

import { Purchases, type CustomerInfo as WebCustomerInfo } from '@revenuecat/purchases-js';
import {
  REVENUECAT_API_KEY,
  REVENUECAT_ENTITLEMENT_ID,
} from '../config/constants';
import { createLogger } from '../utils/logger';

const log = createLogger('Purchases.Web');

// Store the purchases instance
let purchasesInstance: Purchases | null = null;

/**
 * Initialize RevenueCat Web SDK
 */
export async function initializePurchases(): Promise<void> {
  // Don't re-initialize if already done
  if (purchasesInstance) {
    log.debug('Already configured');
    return;
  }

  log.info('Web platform - using purchases-js SDK');
}

/**
 * Login user to RevenueCat (configures SDK with user ID)
 */
export async function loginUser(userId: string): Promise<WebCustomerInfo> {
  try {
    purchasesInstance = Purchases.configure({
      apiKey: REVENUECAT_API_KEY,
      appUserId: userId,
    });
    log.info('Configured for user', { userId });

    return await purchasesInstance.getCustomerInfo();
  } catch (error) {
    log.error('Login failed', error);
    throw error;
  }
}

/**
 * Logout user from RevenueCat
 */
export async function logoutUser(): Promise<void> {
  purchasesInstance = null;
  log.info('User logged out');
}

/**
 * Get current customer info
 */
export async function getCustomerInfo(): Promise<WebCustomerInfo> {
  if (!purchasesInstance) {
    throw new Error('RevenueCat not configured. Call loginUser first.');
  }

  return await purchasesInstance.getCustomerInfo();
}

/**
 * Check if user has active entitlement
 */
export async function hasProEntitlement(): Promise<boolean> {
  if (!purchasesInstance) {
    return false;
  }

  try {
    const customerInfo = await purchasesInstance.getCustomerInfo();
    return REVENUECAT_ENTITLEMENT_ID in customerInfo.entitlements.active;
  } catch (error) {
    log.error('Failed to check entitlement', error);
    return false;
  }
}

/**
 * Get available offerings
 */
export async function getOfferings() {
  if (!purchasesInstance) {
    return null;
  }

  try {
    const offerings = await purchasesInstance.getOfferings();
    return offerings.current || null;
  } catch (error) {
    log.error('Failed to get offerings', error);
    throw error;
  }
}

/**
 * Purchase a package - not directly supported on web, use presentPaywall instead
 */
export async function purchasePackage(_pkg: unknown): Promise<WebCustomerInfo> {
  throw new Error('Use presentPaywall() for web purchases');
}

/**
 * Restore purchases - handled automatically on web
 */
export async function restorePurchases(): Promise<WebCustomerInfo> {
  if (!purchasesInstance) {
    throw new Error('RevenueCat not configured');
  }

  // On web, just refresh customer info
  return await purchasesInstance.getCustomerInfo();
}

/**
 * Present RevenueCat Paywall UI (Web)
 */
export async function presentPaywall(): Promise<boolean> {
  if (!purchasesInstance) {
    log.error('Cannot present paywall - not configured');
    return false;
  }

  try {
    const offerings = await purchasesInstance.getOfferings();
    const currentOffering = offerings.current;

    if (!currentOffering) {
      log.error('No current offering available');
      return false;
    }

    const result = await purchasesInstance.presentPaywall({
      offering: currentOffering
    });

    // Check if purchase was successful
    if (result.customerInfo) {
      const hasEntitlement = REVENUECAT_ENTITLEMENT_ID in result.customerInfo.entitlements.active;
      return hasEntitlement;
    }

    return false;
  } catch (error) {
    log.error('Paywall error', error);
    return false;
  }
}

/**
 * Get management URL - returns RevenueCat customer portal URL
 */
export async function getManagementURL(): Promise<string | null> {
  // RevenueCat web uses Stripe, so management is via Stripe customer portal
  // This would need to be configured in your RevenueCat dashboard
  return null;
}

/**
 * Parse CustomerInfo to subscription status
 */
export function parseSubscriptionStatus(customerInfo: WebCustomerInfo): {
  isActive: boolean;
  plan: 'free' | 'trial' | 'pro';
  expiresAt?: Date;
  productId?: string;
} {
  const entitlement = customerInfo.entitlements.active[REVENUECAT_ENTITLEMENT_ID];

  if (!entitlement) {
    return { isActive: false, plan: 'free' };
  }

  return {
    isActive: true,
    plan: 'pro',
    expiresAt: entitlement.expirationDate ? new Date(entitlement.expirationDate) : undefined,
    productId: entitlement.productIdentifier,
  };
}

/**
 * Check if purchases are configured
 */
export function isPurchasesConfigured(): boolean {
  return purchasesInstance !== null;
}

// Re-export for type compatibility
export type CustomerInfo = WebCustomerInfo;
export type PurchasesOffering = Awaited<ReturnType<Purchases['getOfferings']>>['current'];
export type PurchasesPackage = NonNullable<PurchasesOffering>['availablePackages'][number];
